import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Translate } from '@mui/icons-material';
import {
  StyledContainer,
  StyledPaper,
  StyledSectionHeaderWithBadge,
  StyledTextField,
} from './styles';
import VideoCard from './VideoCard';
import { API_URL } from './config';
import axios from 'axios';
import { trackEvent } from './analytics';

// Using centralized API_URL from config

function SearchPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalVideos, setTotalVideos] = useState(0);
  const [error, setError] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);

  const [sortBy, setSortBy] = useState('outlierScore');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateFilter, setDateFilter] = useState('all');
  const [minOutlierScore, setMinOutlierScore] = useState('all');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showTranslation, setShowTranslation] = useState(false);

  // Fetch folders on mount
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/folders`);
        setFolders(res.data);
      } catch (e) {
        console.error('Error fetching folders:', e);
      }
    };
    fetchFolders();
  }, []);

  // Debounce the query input
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 800);
    return () => clearTimeout(handle);
  }, [query]);

  const searchSnapshot = React.useMemo(
    () => ({
      query: debouncedQuery,
      sortBy,
      order: sortOrder,
      dateFilter,
      minOutlierScore,
      showTranslation,
      folderIds: selectedFolders,
      folderNames: selectedFolders.map((fid) => {
        const f = folders.find((x) => x._id === fid);
        return f?.name || String(fid);
      }),
    }),
    [
      debouncedQuery,
      sortBy,
      sortOrder,
      dateFilter,
      minOutlierScore,
      showTranslation,
      selectedFolders,
      folders,
    ],
  );

  const fetchVideos = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError('');
      }

      const offset = isLoadMore ? videos.length : 0;
      const params = new URLSearchParams({
        sortBy,
        order: sortOrder,
        offset: String(offset),
        limit: String(50),
        dateFilter,
        minOutlierScore,
      });
      if (debouncedQuery && debouncedQuery.length > 0) {
        params.set('query', debouncedQuery);
      }
      if (selectedFolders.length > 0) {
        params.set('folderIds', selectedFolders.join(','));
      }

      const res = await axios.get(`${API_URL}/api/videos?${params.toString()}`);
      const data = res.data;

      if (isLoadMore) {
        setVideos((prev) => [...prev, ...data.videos]);
      } else {
        setVideos(data.videos);
      }
      setHasMore(data.hasMore);
      setTotalVideos(data.total);

      if (isLoadMore) {
        trackEvent('load_more', {
          scope: 'global',
          context: {},
          payload: { ...searchSnapshot, offset: videos.length },
        });
      } else {
        trackEvent('search', {
          scope: 'global',
          context: {},
          payload: { ...searchSnapshot, total: data.total },
        });
      }
    } catch (e) {
      console.error('Error fetching videos:', e);
      setError(e.message || 'Failed to load videos');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch on initial load and when filters change
  useEffect(() => {
    fetchVideos(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, sortBy, sortOrder, dateFilter, minOutlierScore, selectedFolders]);

  const observerRef = React.useRef();
  const loadMoreRef = React.useCallback(
    (node) => {
      if (loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchVideos(true);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadingMore,
      hasMore,
      sortBy,
      sortOrder,
      dateFilter,
      minOutlierScore,
      selectedFolders,
      debouncedQuery,
    ],
  );

  return (
    <Container maxWidth="xl">
      <StyledContainer>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Search
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Search videos across all tracked channels.
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <StyledPaper elevation={0}>
          <StyledSectionHeaderWithBadge>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totalVideos.toLocaleString()} results
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {(videos.some((video) => video.translation) ||
                videos.some((video) => video.channelNameTranslation)) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showTranslation}
                      onChange={(e) => setShowTranslation(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#6366f1',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#6366f1',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(148, 163, 184, 0.3)',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Translate sx={{ fontSize: 18 }} />
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        Translation
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      color: 'text.primary',
                    },
                  }}
                />
              )}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="folder-filter-label" shrink>
                  Folders
                </InputLabel>
                <Box sx={{ position: 'relative' }}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={folders}
                    getOptionLabel={(option) => option.name || ''}
                    value={folders.filter((folder) => selectedFolders.includes(folder._id))}
                    onChange={(event, newValue) => {
                      setSelectedFolders(newValue.map((folder) => folder._id));
                    }}
                    disableCloseOnSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          shrink: true,
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) => {
                      const maxVisible = 1;
                      const visibleTags = value.slice(0, maxVisible);
                      const remainingCount = value.length - maxVisible;

                      return (
                        <>
                          {visibleTags.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option._id}
                              label={option.name}
                              size="small"
                            />
                          ))}
                          {remainingCount > 0 && (
                            <Chip
                              {...getTagProps({ index: maxVisible })}
                              key="remaining-count"
                              label={`+${remainingCount}`}
                              size="small"
                              onDelete={undefined}
                            />
                          )}
                        </>
                      );
                    }}
                    sx={{ minWidth: 180 }}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                  />
                  {selectedFolders.length === 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: 'text.primary',
                        zIndex: 1,
                      }}
                    >
                      All Folders
                    </Box>
                  )}
                </Box>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="date-filter-label">Date range</InputLabel>
                <Select
                  labelId="date-filter-label"
                  value={dateFilter}
                  label="Date range"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="week">Last week</MenuItem>
                  <MenuItem value="2weeks">Last 2 weeks</MenuItem>
                  <MenuItem value="3weeks">Last 3 weeks</MenuItem>
                  <MenuItem value="month">Last month</MenuItem>
                  <MenuItem value="2months">Last 2 months</MenuItem>
                  <MenuItem value="3months">Last 3 months</MenuItem>
                  <MenuItem value="6months">Last 6 months</MenuItem>
                  <MenuItem value="year">Last year</MenuItem>
                  <MenuItem value="3years">Last 3 Years</MenuItem>
                  <MenuItem value="5years">Last 5 Years</MenuItem>
                  <MenuItem value="all">All time</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="min-outlier-score-label">Min Outlier Score</InputLabel>
                <Select
                  labelId="min-outlier-score-label"
                  value={minOutlierScore}
                  label="Min Outlier Score"
                  onChange={(e) => setMinOutlierScore(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="5">5</MenuItem>
                  <MenuItem value="10">10</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="sort-by-label">Sort by</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="outlierScore">Outlier score</MenuItem>
                  <MenuItem value="viewCount">Views</MenuItem>
                  <MenuItem value="publishedTime">Published time</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="sort-order-label">Order</InputLabel>
                <Select
                  labelId="sort-order-label"
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <MenuItem value="desc">Desc</MenuItem>
                  <MenuItem value="asc">Asc</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </StyledSectionHeaderWithBadge>

          <StyledTextField
            fullWidth
            placeholder="Search titles…"
            sx={{ mt: 2 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {loading && videos.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
              {videos.map((video, idx) => (
                <Grid item key={video._id + idx} sx={{ width: '24%', minWidth: '200px' }}>
                  <VideoCard
                    video={video}
                    highlightQuery={debouncedQuery}
                    showTranslation={showTranslation}
                    analyticsContext={{
                      scope: 'global',
                      searchSnapshot,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <div ref={loadMoreRef} />

          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </StyledPaper>
      </StyledContainer>
    </Container>
  );
}

export default SearchPage;
